import { mergePopularidadEncabezado } from './popularidadReportModel';
import { formatPeriodoDescuentosLine } from './descuentosCortesiasReportModel';

export const RELACION_VENTAS_TITULO_DEFAULT = 'REPORTE DE RELACIÓN DE VENTAS';

function foldKeyLoose(k) {
  return String(k)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[_\s-]/g, '')
    .toLowerCase();
}

function keyMatches(spec, rawKey) {
  return spec.patterns.some((re) => re.test(rawKey) || re.test(foldKeyLoose(rawKey)));
}

/**
 * Columnas del impreso (mockup); orden fijo de 14 columnas.
 */
export const RELACION_VENTAS_COLUMN_SPECS = [
  {
    id: 'folio',
    label: 'Folio',
    patterns: [/^folio$/i, /^ticket$/i, /^nocuenta$/i, /^noCuenta$/i, /^numfolio$/i],
    align: 'left',
    kind: 'text',
  },
  {
    id: 'serie',
    label: 'Serie',
    patterns: [/^serie$/i, /^ser$/i],
    align: 'left',
    kind: 'text',
  },
  {
    id: 'tipoDoc',
    label: 'Tipo',
    patterns: [/^tipo$/i, /^tipodoc$/i, /^tipoDocumento$/i, /^tipocomprobante$/i],
    align: 'left',
    kind: 'text',
  },
  {
    id: 'mesa',
    label: 'Mesa',
    patterns: [/^mesa$/i, /^numeromesa$/i, /^nromesa$/i, /^noMesa$/i],
    align: 'left',
    kind: 'text',
  },
  {
    id: 'alimentos',
    label: 'Alimentos',
    patterns: [/^alimentos$/i, /^comida$/i, /^food$/i, /^importealimentos$/i],
    align: 'right',
    total: true,
    kind: 'money',
  },
  {
    id: 'bebidas',
    label: 'Bebidas',
    patterns: [/^bebidas$/i, /^importebebidas$/i, /^drinks$/i],
    align: 'right',
    total: true,
    kind: 'money',
  },
  {
    id: 'otros',
    label: 'Otros',
    patterns: [/^otros$/i, /^importeotros$/i],
    align: 'right',
    total: true,
    kind: 'money',
  },
  {
    id: 'descuento',
    label: 'Descuento',
    patterns: [/^descuento$/i, /^descuentos$/i, /^importedescuento$/i],
    align: 'right',
    total: true,
    kind: 'money',
  },
  {
    id: 'total',
    label: 'Total',
    patterns: [/^total$/i, /^importetotal$/i, /^totalventa$/i, /^granTotal$/i],
    align: 'right',
    total: true,
    kind: 'money',
  },
  {
    id: 'propina',
    label: 'Propina',
    patterns: [/^propina$/i, /^tip$/i, /^importepropina$/i],
    align: 'right',
    total: true,
    kind: 'money',
  },
  {
    id: 'pax',
    label: 'Pax',
    patterns: [/^pax$/i, /^personas$/i, /^comensales$/i, /^covers$/i],
    align: 'right',
    total: true,
    kind: 'pax',
  },
  {
    id: 'tipoPago',
    label: 'Tipo Pago',
    patterns: [/^tipopago$/i, /^formaPago$/i, /^formapago$/i, /^metodopago$/i, /^pago$/i],
    align: 'left',
    kind: 'text',
  },
  {
    id: 'mesero',
    label: 'mesero',
    patterns: [/^mesero$/i, /^idmesero$/i, /^numeromesero$/i, /^camarero$/i, /^idMesero$/i],
    align: 'left',
    kind: 'text',
  },
  {
    id: 'socio',
    label: 'socio',
    patterns: [/^socio$/i, /^idsocio$/i, /^nosocio$/i, /^idSocio$/i],
    align: 'left',
    kind: 'text',
  },
];

/** @param {Record<string, unknown>|null|undefined} sampleRow */
export function resolveRelacionVentasColumns(sampleRow) {
  const keys =
    sampleRow && typeof sampleRow === 'object' && !Array.isArray(sampleRow)
      ? Object.keys(sampleRow)
      : [];
  const used = new Set();
  return RELACION_VENTAS_COLUMN_SPECS.map((spec) => {
    const k = keys.find((kk) => !used.has(kk) && keyMatches(spec, kk));
    if (k) used.add(k);
    return { ...spec, sourceKey: k ?? null };
  });
}

/** @param {Record<string, unknown>} raw @param {ReturnType<typeof resolveRelacionVentasColumns>} resolved */
export function rowToRelacionNorm(raw, resolved) {
  const o = {};
  for (const c of resolved) {
    o[c.id] = c.sourceKey ? raw[c.sourceKey] : undefined;
  }
  return o;
}

function fmtMoney2(n) {
  return n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatRelacionCell(spec, value) {
  if (value == null || value === '') return '—';
  if (spec.kind === 'money') {
    const n = Number(value);
    return Number.isNaN(n) ? String(value) : fmtMoney2(n);
  }
  if (spec.kind === 'pax') {
    const n = Number(value);
    return Number.isNaN(n) ? String(value) : String(Math.round(n));
  }
  return String(value);
}

export function formatRelacionTotalCell(spec, value) {
  if (!spec.total) return '—';
  if (value == null || Number.isNaN(Number(value))) return '—';
  return formatRelacionCell(
    { ...spec, kind: spec.id === 'pax' ? 'pax' : 'money' },
    value
  );
}

/** @param {Record<string, unknown>[]} normRows @param {ReturnType<typeof resolveRelacionVentasColumns>} resolved */
export function sumRelacionTotals(normRows, resolved) {
  const totals = {};
  for (const c of resolved) {
    if (!c.total) continue;
    let s = 0;
    for (const r of normRows) {
      const n = Number(r[c.id]);
      if (!Number.isNaN(n)) s += n;
    }
    totals[c.id] = s;
  }
  return totals;
}

export function mergeRelacionTotalsSlice(computed, apiSlice, resolved) {
  const out = { ...computed };
  if (!apiSlice || typeof apiSlice !== 'object') return out;
  for (const c of resolved) {
    if (!c.total) continue;
    const candidates = [
      apiSlice[c.id],
      c.sourceKey ? apiSlice[c.sourceKey] : undefined,
    ];
    for (const raw of candidates) {
      if (raw == null || raw === '') continue;
      const x = Number(raw);
      if (!Number.isNaN(x)) {
        out[c.id] = x;
        break;
      }
    }
  }
  return out;
}

/**
 * @param {string} label
 * @param {ReturnType<typeof resolveRelacionVentasColumns>} resolved
 * @param {Record<string, number>} totalsById
 */
export function buildRelacionFootCells(resolved, label, totalsById) {
  return resolved.map((c, i) => {
    if (i === 0) return label;
    if (!c.total) return '';
    const n = totalsById[c.id];
    if (n == null || Number.isNaN(Number(n))) return '—';
    return formatRelacionTotalCell(c, n);
  });
}

export function turnoGroupKey(raw) {
  if (!raw || typeof raw !== 'object') return '';
  const turno = raw.turno ?? raw.Turno;
  const fechaRaw = raw.fechaTurno ?? raw.fecha_turno;
  if (turno == null && !fechaRaw) return '';
  const fecha = fechaRaw ? String(fechaRaw).trim().slice(0, 10) : '';
  const t = turno == null || turno === '' ? '' : String(Math.trunc(Number(turno)));
  return `${fecha}|${t}`;
}

export function turnoGroupBanner(raw) {
  if (!raw || typeof raw !== 'object') return 'Turno: —';
  const nombre = raw.turnoNombre ?? raw.turno_nombre;
  if (nombre != null && String(nombre).trim()) {
    return `Turno: ${String(nombre).trim()}`;
  }
  const turno = raw.turno ?? raw.Turno;
  if (turno != null && String(turno).trim() !== '') {
    return `Turno: ${String(turno).trim()}`;
  }
  return 'Turno: —';
}

export function cajaGroupKey(raw) {
  if (!raw || typeof raw !== 'object') return '';
  const id = raw.cajaId ?? raw.idCaja ?? raw.caja;
  const nom = raw.nombreCaja ?? raw.cajaNombre ?? raw.nombre_caja ?? raw.descripcionCaja;
  if (nom != null && String(nom).trim()) return String(nom).trim();
  if (id != null && String(id).trim() !== '') return `id:${String(id)}`;
  return '';
}

export function cajaGroupBanner(raw) {
  if (!raw || typeof raw !== 'object') return 'Caja';
  const nom =
    raw.nombreCaja ?? raw.cajaNombre ?? raw.cajaDescripcion ?? raw.descripcionCaja ?? raw.nombreCajaDescripcion;
  if (nom != null && String(nom).trim()) {
    const s = String(nom).trim();
    if (/^caja\b/i.test(s)) return s;
    return `Caja ${s}`;
  }
  const id = raw.cajaId ?? raw.idCaja ?? raw.caja;
  if (id != null && String(id).trim() !== '') return `Caja ${id}`;
  return 'Caja';
}

/**
 * @param {Record<string, unknown>[]} rawRows
 * @param {ReturnType<typeof resolveRelacionVentasColumns>} resolved
 * @param {boolean} ordenarMesa
 */
export function sortRelacionRowsForDisplay(rawRows, resolved, ordenarMesa) {
  if (!rawRows.length) return rawRows;
  const folioSpec = resolved.find((c) => c.id === 'folio');
  const mesaSpec = resolved.find((c) => c.id === 'mesa');
  const skFolio = folioSpec?.sourceKey;
  const skMesa = mesaSpec?.sourceKey;

  return [...rawRows].sort((a, b) => {
    const ta = turnoGroupKey(a).localeCompare(turnoGroupKey(b), 'es', { numeric: true });
    if (ta !== 0) return ta;
    const ca = cajaGroupKey(a).localeCompare(cajaGroupKey(b), 'es', { sensitivity: 'base' });
    if (ca !== 0) return ca;
    if (ordenarMesa && skMesa) {
      const va = a[skMesa];
      const vb = b[skMesa];
      const na = Number(va);
      const nb = Number(vb);
      if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
      return String(va ?? '').localeCompare(String(vb ?? ''), 'es', { numeric: true });
    }
    if (skFolio) {
      return String(a[skFolio] ?? '').localeCompare(String(b[skFolio] ?? ''), 'es', {
        numeric: true,
      });
    }
    return 0;
  });
}

/**
 * @param {{
 *   kind: 'turnGroup',
 *   label: string,
 * } | {
 *   kind: 'group',
 *   label: string,
 * } | {
 *   kind: 'row',
 *   raw: Record<string, unknown>,
 * }} item
 */
export function buildRelacionSegments(sortedRawRows, agruparTurno = false) {
  /** @type {{ kind: 'turnGroup' | 'group' | 'row', label?: string, raw?: Record<string, unknown> }[]} */
  const segments = [];
  let lastTurnKey = undefined;
  let lastCajaKey = undefined;

  for (const raw of sortedRawRows) {
    if (agruparTurno) {
      const turnKey = turnoGroupKey(raw);
      const cajaKey = cajaGroupKey(raw);

      if (turnKey && turnKey !== lastTurnKey) {
        lastTurnKey = turnKey;
        lastCajaKey = undefined;
        segments.push({ kind: 'turnGroup', label: turnoGroupBanner(raw) });
      }

      if (cajaKey !== lastCajaKey) {
        lastCajaKey = cajaKey;
        segments.push({ kind: 'group', label: cajaGroupBanner(raw) });
      }
    }

    segments.push({ kind: 'row', raw });
  }

  return segments;
}

export function mergeRelacionVentasEncabezado(apiEnc, encabezadoFallback) {
  return mergePopularidadEncabezado(apiEnc, encabezadoFallback, {
    reporteTitulo: RELACION_VENTAS_TITULO_DEFAULT,
    incluirCortesias: false,
    lineaCortesias: '',
    cortesiasSubtitle: '',
  });
}

export function withRelacionVentasPeriodoLine(enc, fechaInicio, fechaFin) {
  const e = enc && typeof enc === 'object' ? { ...enc } : {};
  if (!String(e.periodoTexto || '').trim() && (fechaInicio || fechaFin)) {
    e.periodoTexto = formatPeriodoDescuentosLine(fechaInicio, fechaFin);
  }
  return e;
}

/** @param {ReturnType<typeof resolveRelacionVentasColumns>} resolved @param {Record<string, string>|null|undefined} columnas */
export function applyRelacionVentasColumnLabelsFromApi(resolved, columnas) {
  if (!columnas || typeof columnas !== 'object' || Array.isArray(columnas)) return resolved;
  return resolved.map((c) => {
    if (!c.sourceKey) return c;
    const label = columnas[c.sourceKey];
    if (typeof label === 'string' && label.trim()) return { ...c, label: label.trim() };
    return c;
  });
}

/**
 * @param {Record<string, unknown>|null|undefined} apiTotales objeto completo (p. ej. totalVenta / totalCortesia)
 */
export function extractRelacionApiFootTotals(apiTotales) {
  if (!apiTotales || typeof apiTotales !== 'object') {
    return { venta: null, cortesia: null };
  }
  const venta =
    apiTotales.totalVenta ??
    apiTotales.total_venta ??
    apiTotales.venta ??
    apiTotales.totalesVenta ??
    null;
  const cortesia =
    apiTotales.totalCortesia ??
    apiTotales.total_cortesia ??
    apiTotales.cortesia ??
    apiTotales.totalesCortesia ??
    null;
  return {
    venta: venta && typeof venta === 'object' ? venta : null,
    cortesia: cortesia && typeof cortesia === 'object' ? cortesia : null,
  };
}
